"use client";

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

const REFRESH_INTERVAL_MS = 60_000;

type Options = {
  enabled?: boolean;
};

async function fetchAdminProductsAttentionCount(): Promise<number> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    return 0;
  }

  const res = await fetch("/api/admin/products/attention-count", {
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (!res.ok) {
    return 0;
  }
  const data = (await res.json()) as { count?: number };
  return typeof data.count === "number" ? data.count : 0;
}

/**
 * Polls GET /api/admin/products/attention-count for nav badge and filter labels.
 */
export function useAdminProductsAttentionCount(options?: Options) {
  const enabled = options?.enabled ?? true;
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!enabled) return;

    setIsLoading(true);
    try {
      const next = await fetchAdminProductsAttentionCount();
      setCount(next);
    } catch (error) {
      console.error("useAdminProductsAttentionCount:", error);
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    const load = async () => {
      try {
        const next = await fetchAdminProductsAttentionCount();
        if (!cancelled) {
          setCount(next);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("useAdminProductsAttentionCount:", error);
        }
      }
    };

    void load();
    const id = window.setInterval(() => {
      void load();
    }, REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [enabled]);

  return { count, isLoading, refresh };
}
